/**
 * CR327: Debounced Input Hook
 * 
 * Provides local state for text inputs that debounces commits to parent.
 * Prevents preview jitter when typing in editors.
 * 
 * Features:
 * - Immediate local state updates (responsive typing)
 * - Debounced commits to parent (prevents preview thrash)
 * - Commit on blur for immediate save when leaving field
 * - Reset when external value changes
 */

import { useState, useEffect, useRef, useCallback } from "react";

interface UseDebouncedInputOptions {
  /** Debounce delay in ms (default: 250) */
  debounceMs?: number;
  /** Commit immediately on blur (default: true) */
  commitOnBlur?: boolean;
}

export interface DebouncedInputState {
  /** Current local value (for input binding) */
  localValue: string;
  /** Handler for input onChange */
  onChange: (value: string) => void;
  /** Handler for input onBlur */
  onBlur: () => void;
  /** Whether a debounced commit is pending */
  isPending: boolean;
  /** Force immediate commit */
  flush: () => void;
}

/**
 * Hook for debounced text input that doesn't cause preview jitter.
 * 
 * @param externalValue - The controlled value from parent state
 * @param onCommit - Callback when value should be committed (debounced)
 * @param options - Configuration options
 */
export function useDebouncedInput(
  externalValue: string,
  onCommit: (value: string) => void,
  options: UseDebouncedInputOptions = {}
): DebouncedInputState {
  const { debounceMs = 250, commitOnBlur = true } = options;
  
  // Local state for immediate input feedback
  const [localValue, setLocalValue] = useState(externalValue);
  const [isPending, setIsPending] = useState(false);
  
  // Track the last committed value to detect external changes
  const lastCommittedRef = useRef(externalValue);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  
  // Sync local value when external value changes (from outside)
  useEffect(() => {
    if (externalValue !== lastCommittedRef.current) {
      // External change - sync local state
      setLocalValue(externalValue);
      lastCommittedRef.current = externalValue;
      setIsPending(false);
      
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [externalValue]);
  
  // Commit function
  const commit = useCallback((value: string) => {
    if (value !== lastCommittedRef.current) {
      lastCommittedRef.current = value;
      onCommitRef.current(value);
    }
    setIsPending(false);
  }, []);
  
  // Flush pending changes immediately
  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    commit(localValue);
  }, [localValue, commit]);
  
  // Handle input change
  const onChange = useCallback((value: string) => {
    setLocalValue(value);
    setIsPending(true);
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new debounced commit
    timeoutRef.current = setTimeout(() => {
      commit(value);
    }, debounceMs);
  }, [debounceMs, commit]);
  
  // Handle blur
  const onBlur = useCallback(() => {
    if (commitOnBlur && isPending) {
      flush();
    }
  }, [commitOnBlur, isPending, flush]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return {
    localValue,
    onChange,
    onBlur,
    isPending,
    flush,
  };
}

/**
 * Shorthand for nullable string inputs
 */
export function useDebouncedNullableInput(
  externalValue: string | null,
  onCommit: (value: string | null) => void,
  options: UseDebouncedInputOptions = {}
): DebouncedInputState {
  const state = useDebouncedInput(
    externalValue ?? "",
    (value) => onCommit(value || null),
    options
  );
  return state;
}
